import { onScopeDispose, ref, shallowRef, watch } from 'vue'
import type { PagePointer } from 'models'
import type { PageSelection } from '~/pageTools/pointing'

/** How long the selection rests before the bubble shows, so it does not chase the drag. */
const SELECTION_SETTLE_MS = 150
/** The bubble's distance below the selection. */
const BUBBLE_GAP = 8
/** The room the bubble needs inside the window. */
const BUBBLE_WIDTH = 120
const BUBBLE_HEIGHT = 36

/** What the page tools give the pointer: see `usePageTools`. */
export type PagePointing = {
    /** The element a gesture at a point of the window stands for, if any. */
    at: (x: number, y: number) => Element | undefined
    describe: (element: Element, selected?: string) => PagePointer
    selection: () => PageSelection | undefined
    label: (element: Element) => string
}

/** Where the box goes on the element under the pointer. */
export type PagePointerBox = {
    top: number
    left: number
    width: number
    height: number
    label: string
}

/**
 * The user pointing at the page for the next message (spec
 * `page-point-and-choose.md`, P). `start` lets the user choose an element:
 * the widget lays a layer of its own over the page, which takes the pointer
 * (`move`, `press`), so no event of the gesture ever reaches the site; the
 * box follows the element under it, a press chooses, Esc gives up. While the
 * chat is open, a text the user selects on the page gets a bubble that sends
 * it along. Both only while `available`.
 */
export function usePagePointer({
    pointing,
    available,
    chatOpen,
    onPointed,
}: {
    pointing: PagePointing
    /** The page tools are on, the chat can send what is pointed at, and no answer is running. */
    available: () => boolean
    /** The chat is open: the bubble shows only then. */
    chatOpen: () => boolean
    onPointed: (pointer: PagePointer) => void
}) {
    const picking = ref(false)
    const box = ref<PagePointerBox | undefined>()
    const bubble = ref<{ top: number; left: number } | undefined>()
    let hovered: Element | undefined
    const selected = shallowRef<PageSelection | undefined>()

    const place = (element: Element | undefined) => {
        hovered = element
        if (!element) {
            box.value = undefined
            return
        }
        const rect = element.getBoundingClientRect()
        box.value = {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            label: pointing.label(element),
        }
    }

    let frame = 0
    /** The pointer over the layer at a point of the window: the box goes to what lies under it. */
    const move = (x: number, y: number) => {
        cancelAnimationFrame(frame)
        frame = requestAnimationFrame(() => place(pointing.at(x, y)))
    }

    /** A press on the layer: chooses what lies under it, if anything may be pointed at there. */
    const press = (x: number, y: number) => {
        const element = pointing.at(x, y)
        if (element) {
            choose(element)
        } else {
            place(undefined)
        }
    }

    const onKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            stop()
        }
    }

    const onScroll = () => place(hovered?.isConnected ? hovered : undefined)

    const stop = () => {
        if (!picking.value) {
            return
        }
        picking.value = false
        window.removeEventListener('keydown', onKey, true)
        window.removeEventListener('scroll', onScroll, true)
        cancelAnimationFrame(frame)
        place(undefined)
    }

    const choose = (element: Element) => {
        stop()
        onPointed(pointing.describe(element))
    }

    /** Lets the user choose an element of the page; again, gives up. */
    const start = () => {
        if (picking.value) {
            stop()
            return
        }
        if (!available()) {
            return
        }
        bubble.value = undefined
        picking.value = true
        window.addEventListener('keydown', onKey, true)
        window.addEventListener('scroll', onScroll, true)
    }

    // The bubble of a selected text: a pointer that hovers, so not on a
    // touch screen, where the browser has its own menu on a selection
    const finePointer =
        typeof matchMedia === 'function' &&
        matchMedia('(hover: hover) and (pointer: fine)').matches
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    const readSelection = () => {
        // A modal dialog of the page leaves the widget inert: a bubble there
        // could not be pressed
        const current =
            available() &&
            chatOpen() &&
            !picking.value &&
            !document.querySelector('dialog:modal')
                ? pointing.selection()
                : undefined
        selected.value = current
        if (!current) {
            bubble.value = undefined
            return
        }
        bubble.value = {
            top: Math.min(
                current.rect.bottom + BUBBLE_GAP,
                window.innerHeight - BUBBLE_HEIGHT,
            ),
            left: Math.max(
                0,
                Math.min(current.rect.right, window.innerWidth - BUBBLE_WIDTH),
            ),
        }
    }
    const onSelection = () => {
        clearTimeout(settleTimer)
        bubble.value = undefined
        settleTimer = setTimeout(readSelection, SELECTION_SETTLE_MS)
    }
    const onPageScroll = () => {
        if (bubble.value) {
            readSelection()
        }
    }
    if (finePointer) {
        document.addEventListener('selectionchange', onSelection)
        window.addEventListener('scroll', onPageScroll, true)
    }

    /** Sends the selected text along with the next message. */
    const ask = () => {
        const current = selected.value
        bubble.value = undefined
        if (current?.element.isConnected) {
            onPointed(pointing.describe(current.element, current.text))
        }
    }

    watch(
        () => available() && chatOpen(),
        (on) => {
            if (!on) {
                stop()
                bubble.value = undefined
            }
        },
    )

    onScopeDispose(() => {
        stop()
        clearTimeout(settleTimer)
        if (finePointer) {
            document.removeEventListener('selectionchange', onSelection)
            window.removeEventListener('scroll', onPageScroll, true)
        }
    })

    return { picking, box, bubble, start, stop, ask, move, press }
}
